import { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, Loader2, Building2, ArrowRightCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { useTenant } from "@/contexts/TenantContext";

export default function TenantsPage() {
  const { switchTenant } = useTenant();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try { const r = await api.get("/tenants"); setRows(r.data || []); }
    catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function startNew() { setForm({ subdomain: "", name: "", status: "active" }); setEditing("new"); }
  function startEdit(t) { setForm({ ...t }); setEditing(t); }
  async function save() {
    if (!form.name || (editing === "new" && !form.subdomain)) { toast.error("Name and subdomain required"); return; }
    setSaving(true);
    try {
      if (editing === "new") await api.post("/tenants", { name: form.name, subdomain: form.subdomain, status: form.status||"active" });
      else await api.patch(`/tenants/${editing.id}`, { name: form.name, status: form.status });
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  }
  async function remove(t) {
    if (t.is_operator) { toast.error("Cannot delete operator tenant"); return; }
    if (!confirm(`Soft-delete tenant ${t.name}?`)) return;
    try { await api.delete(`/tenants/${t.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  }

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="tenants-title">Tenants</h1>
          <p className="text-sm text-muted-foreground mt-1">Operator-only. Create customer tenants, impersonate to administer them, soft-delete.</p>
        </div>
        <Button data-testid="add-tenant-btn" onClick={startNew} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"><Plus className="w-4 h-4"/> New tenant</Button>
      </div>
      <Card className="overflow-hidden">
        {loading ? <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground"><tr className="text-left">
              <th className="px-4 py-2">Tenant</th><th className="px-4 py-2">Subdomain</th><th className="px-4 py-2">Status</th><th className="px-4 py-2">Operator?</th><th className="px-4 py-2 w-44">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} className="border-t border-border hover:bg-muted/30" data-testid={`row-tenant-${t.id}`}>
                  <td className="px-4 py-2.5"><div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-primary" /><span className="font-medium">{t.name}</span></div></td>
                  <td className="px-4 py-2.5"><code className="text-xs">{t.subdomain}</code></td>
                  <td className="px-4 py-2.5"><Badge variant={t.status==="active"?"outline":"destructive"}>{t.status}</Badge></td>
                  <td className="px-4 py-2.5">{t.is_operator ? <Badge>Operator</Badge> : "—"}</td>
                  <td className="px-4 py-2.5"><div className="flex items-center gap-1">
                    <Button data-testid={`impersonate-${t.id}`} onClick={()=>switchTenant(t.id)} variant="ghost" size="sm" className="h-8 gap-1"><ArrowRightCircle className="w-4 h-4"/>Enter</Button>
                    <Button data-testid={`edit-tenant-${t.id}`} onClick={()=>startEdit(t)} variant="ghost" size="icon" className="h-8 w-8"><Edit2 className="w-4 h-4"/></Button>
                    {!t.is_operator && <Button data-testid={`del-tenant-${t.id}`} onClick={()=>remove(t)} variant="ghost" size="icon" className="h-8 w-8 text-destructive"><Trash2 className="w-4 h-4"/></Button>}
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      <Dialog open={!!editing} onOpenChange={(v)=>!v&&setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing==="new"?"New tenant":`Edit ${editing?.name}`}</DialogTitle><DialogDescription>{editing==="new"?"Subdomain cannot be changed later.":"Update name or status."}</DialogDescription></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3 py-2">
            <div><Label>Name</Label><Input data-testid="tenant-name" value={form.name||""} onChange={(e)=>setForm({...form, name:e.target.value})} /></div>
            <div><Label>Subdomain</Label><Input data-testid="tenant-subdomain" disabled={editing!=="new"} value={form.subdomain||""} onChange={(e)=>setForm({...form, subdomain:e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,"")})} /></div>
            <div className="sm:col-span-2"><Label>Status</Label><Input data-testid="tenant-status" value={form.status||"active"} onChange={(e)=>setForm({...form, status:e.target.value})} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setEditing(null)}>Cancel</Button><Button data-testid="tenant-save" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground">{saving?<Loader2 className="w-4 h-4 animate-spin"/>:"Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
